export interface GanttV5Interval {
  id: string;
  start: number;
  end: number;
}

/** Numeric max multiset with O(log n) add/remove and amortized O(log n) max cleanup. */
export class GanttV5MaxMultiset {
  private readonly counts = new Map<number, number>();

  private readonly heap: number[] = [];

  add(value: number): void {
    this.counts.set(value, (this.counts.get(value) ?? 0) + 1);
    this.heap.push(value);
    let index = this.heap.length - 1;
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.heap[parent] >= this.heap[index]) break;
      swap(this.heap, parent, index);
      index = parent;
    }
  }

  remove(value: number): void {
    const count = this.counts.get(value) ?? 0;
    if (count <= 1) this.counts.delete(value);
    else this.counts.set(value, count - 1);
  }

  maximum(): number {
    while (this.heap.length > 0 && !this.counts.has(this.heap[0])) this.popRoot();
    return this.heap[0] ?? 0;
  }

  private popRoot(): void {
    const tail = this.heap.pop();
    if (tail === undefined || this.heap.length === 0) return;
    this.heap[0] = tail;
    let index = 0;
    while (true) {
      const left = index * 2 + 1;
      const right = left + 1;
      let largest = index;
      if (left < this.heap.length && this.heap[left] > this.heap[largest]) largest = left;
      if (right < this.heap.length && this.heap[right] > this.heap[largest]) largest = right;
      if (largest === index) return;
      swap(this.heap, index, largest);
      index = largest;
    }
  }
}

interface IntervalLayer<T> {
  index: number;
  end: number;
  tasks: T[];
}

const swap = <T,>(values: T[], left: number, right: number) => {
  const value = values[left];
  values[left] = values[right];
  values[right] = value;
};

const pushLayer = <T,>(heap: Array<IntervalLayer<T>>, layer: IntervalLayer<T>) => {
  heap.push(layer);
  let index = heap.length - 1;
  while (index > 0) {
    const parent = Math.floor((index - 1) / 2);
    if (heap[parent].end <= heap[index].end) break;
    swap(heap, parent, index);
    index = parent;
  }
};

const popLayer = <T,>(heap: Array<IntervalLayer<T>>): IntervalLayer<T> | undefined => {
  const root = heap[0];
  const tail = heap.pop();
  if (!root || !tail || heap.length === 0) return root;
  heap[0] = tail;
  let index = 0;
  while (true) {
    const left = index * 2 + 1;
    const right = left + 1;
    let smallest = index;
    if (left < heap.length && heap[left].end < heap[smallest].end) smallest = left;
    if (right < heap.length && heap[right].end < heap[smallest].end) smallest = right;
    if (smallest === index) break;
    swap(heap, index, smallest);
    index = smallest;
  }
  return root;
};

/** Partition intervals into non-overlapping lanes in O(n log n). */
export function partitionGanttV5IntervalsIntoLayers<T extends Pick<GanttV5Interval, 'start' | 'end'>>(
  tasks: T[],
): T[][] {
  if (tasks.length === 0) return [];
  const sorted = [...tasks].sort((a, b) => a.start - b.start || a.end - b.end);
  const heap: Array<IntervalLayer<T>> = [];
  const layers: Array<IntervalLayer<T>> = [];

  for (const task of sorted) {
    const reusable = heap[0]?.end <= task.start ? popLayer(heap) : undefined;
    const layer = reusable ?? { index: layers.length, end: task.end, tasks: [] };
    if (!reusable) layers.push(layer);
    layer.tasks.push(task);
    layer.end = task.end;
    pushLayer(heap, layer);
  }

  return [...layers].sort((a, b) => a.index - b.index).map((layer) => layer.tasks);
}

/** Return IDs that overlap at least one sibling, using a sorted sweep in O(n log n). */
export function findGanttV5OverlappingIntervalIds<T extends GanttV5Interval>(tasks: T[]): Set<string> {
  if (tasks.length <= 1) return new Set<string>();
  const sorted = [...tasks].sort((a, b) => a.start - b.start || a.end - b.end);
  const overlapping = new Set<string>();
  let furthestEnding: T | null = null;

  for (const task of sorted) {
    if (furthestEnding && furthestEnding.end > task.start && task.end > furthestEnding.start) {
      overlapping.add(furthestEnding.id);
      overlapping.add(task.id);
    }
    if (!furthestEnding || task.end > furthestEnding.end) furthestEnding = task;
  }
  return overlapping;
}

/** Calculate the maximum concurrent load. End events precede starts at equal times. */
export function calculateGanttV5PeakLoad<T extends Pick<GanttV5Interval, 'start' | 'end'>>(
  tasks: T[],
  readLoad: (task: T) => number,
): number {
  const events: Array<{ time: number; delta: number }> = [];
  for (const task of tasks) {
    const load = Math.max(0, Number(readLoad(task)) || 0);
    if (load === 0 || task.end <= task.start) continue;
    events.push({ time: task.start, delta: load }, { time: task.end, delta: -load });
  }
  events.sort((a, b) => a.time - b.time || a.delta - b.delta);
  let current = 0;
  let peak = 0;
  for (const event of events) {
    current += event.delta;
    peak = Math.max(peak, current);
  }
  return peak;
}
